import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { FooterButtons } from '../../../shared/components/Modals/FooterButtons';
import classNames from 'classnames';
import Checkbox from 'rc-checkbox';
import { ConfirmationModal } from './ConfirmationModal';

const pdfFileIcon = `${process.env.PUBLIC_URL}/img/pdf-file-icon.png`;
const attachmentIcon = `${process.env.PUBLIC_URL}/img/icons/file-attachment-icon.png`;
const MAX_MESSAGE_LENGTH = 10000;
const MAX_IMAGES = 5;
const MAX_PDFS = 5;

const RemoveIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 2L2 10M10 10L2 2"
      stroke="#17191A"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

const Spinner = () => (
  <svg
    fill="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g>
      <circle cx="12" cy="2.5" r="1.5" opacity=".14" />
      <circle cx="16.75" cy="3.77" r="1.5" opacity=".29" />
      <circle cx="20.23" cy="7.25" r="1.5" opacity=".43" />
      <circle cx="21.50" cy="12.00" r="1.5" opacity=".57" />
      <circle cx="20.23" cy="16.75" r="1.5" opacity=".71" />
      <circle cx="16.75" cy="20.23" r="1.5" opacity=".86" />
      <circle cx="12" cy="21.5" r="1.5" />
      <animateTransform
        attributeName="transform"
        type="rotate"
        calcMode="discrete"
        dur="0.75s"
        values="0 12 12;30 12 12;60 12 12;90 12 12;120 12 12;150 12 12;180 12 12;210 12 12;240 12 12;270 12 12;300 12 12;330 12 12;360 12 12"
        repeatCount="indefinite"
      />
    </g>
  </svg>
);

export function CreateMessageModal({
  isOpen,
  setIsOpen,
  title = 'Send Message to Pet Parent',
  contextSubheading = '',
  modalSize = 'md',
  modalClassName = '',
  contentClassName = '',
  onSave,
  isLoading,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isResponseRequired, setIsResponseRequired] = useState(false);
  const [attachments, setAttachments] = useState({ images: [], pdfs: [] });
  const [fileUploadErrors, setFileUploadErrors] = useState([]);
  const [textAboveButtons, setTextAboveButtons] = useState('');

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const isMessageTooLong = inputValue.length > MAX_MESSAGE_LENGTH;
  const hasAttachments =
    attachments.images.length > 0 || attachments.pdfs.length > 0;
  const hasUnsavedContent =
    Boolean(inputValue) || hasAttachments || isResponseRequired;

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const validImageTypes = ['image/jpeg', 'image/png'];
    const validPdfTypes = ['application/pdf'];

    setTextAboveButtons('');
    const newAttachments = { images: [], pdfs: [] };
    const errorSet = new Set();

    files.forEach((file) => {
      if (validImageTypes.includes(file.type)) {
        const total = attachments.images.length + newAttachments.images.length;
        if (total >= MAX_IMAGES) {
          errorSet.add(`You can upload maximum ${MAX_IMAGES} images`);
          return;
        }
        newAttachments.images.push(file);
        return;
      }

      if (validPdfTypes.includes(file.type)) {
        const total = attachments.pdfs.length + newAttachments.pdfs.length;
        if (total >= MAX_PDFS) {
          errorSet.add(`You can upload maximum ${MAX_PDFS} PDFs`);
          return;
        }
        newAttachments.pdfs.push(file);
        return;
      }

      errorSet.add(
        'Invalid file type. Only images (JPEG, PNG) or PDFs allowed.',
      );
    });

    setFileUploadErrors(Array.from(errorSet));

    if (newAttachments.images.length || newAttachments.pdfs.length) {
      setAttachments((prev) => ({
        images: [...prev.images, ...newAttachments.images],
        pdfs: [...prev.pdfs, ...newAttachments.pdfs],
      }));
    }

    event.target.value = null;
  };

  const removeAttachment = (type, index) => {
    setAttachments((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
    setFileUploadErrors([]);
  };

  const handleReset = useCallback(() => {
    setInputValue('');
    setIsResponseRequired(false);
    setAttachments({ images: [], pdfs: [] });
    setFileUploadErrors([]);
    setTextAboveButtons('');
  }, []);

  const handleClose = useCallback(() => {
    setIsConfirmModalOpen(false);
    setIsOpen(false);
    handleReset();
  }, [handleReset, setIsOpen]);

  const requestClose = useCallback(() => {
    if (isLoading) {
      return;
    }
    if (hasUnsavedContent) {
      setIsConfirmModalOpen(true);
      return;
    }
    handleClose();
  }, [handleClose, hasUnsavedContent, isLoading]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key !== 'Escape' && event.keyCode !== 27) {
        return;
      }
      if (event.defaultPrevented) {
        return;
      }

      event.preventDefault();

      if (isConfirmModalOpen) {
        setIsConfirmModalOpen(false);
        return;
      }

      requestClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isConfirmModalOpen, requestClose]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isOpen]);

  const handleSave = () => {
    if (
      !inputValue &&
      attachments.images.length === 0 &&
      attachments.pdfs.length === 0
    ) {
      setTextAboveButtons(
        'Please enter a message or add at least one attachment before sending.',
      );
      return;
    }

    onSave({
      message: inputValue,
      requiresResponse: isResponseRequired,
      attachments,
      onSuccess: () => handleReset(),
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        size={modalSize}
        className={classNames(
          'nested-modal message-with-attachments-modal model-form centered',
          modalClassName,
        )}
      >
        <ModalHeader>
          <div className="message-modal-header-content">
            <h3>{title}</h3>
            {contextSubheading ? (
              <p
                className="message-modal-context-subheading"
                title={contextSubheading}
              >
                {contextSubheading}
              </p>
            ) : null}
          </div>
        </ModalHeader>
        <ModalBody>
          <div className={classNames('message-container', contentClassName)}>
            <div className="textarea-container">
              <textarea
                ref={textareaRef}
                className="message-textarea"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  if (e.target.value && textAboveButtons) {
                    setTextAboveButtons('');
                  }
                }}
                placeholder="Enter message to Pet Parent..."
                disabled={isLoading}
              />

              {isMessageTooLong && (
                <div
                  className={classNames(
                    'message-length-indicator',
                    isMessageTooLong && 'text-danger',
                  )}
                >
                  <span className="indicator-error">
                    Text cannot exceed{' '}
                    {MAX_MESSAGE_LENGTH.toLocaleString('en-US')} characters.
                  </span>

                  <span className="indicator-count">
                    {inputValue.length.toLocaleString('en-US')} /{' '}
                    {MAX_MESSAGE_LENGTH.toLocaleString('en-US')}
                  </span>
                </div>
              )}
            </div>

            <div className="message-controls-row">
              <label className="checkbox message-await-reply">
                <Checkbox
                  disabled={isLoading}
                  checked={isResponseRequired}
                  onChange={(e) => setIsResponseRequired(e.target.checked)}
                />
                <span className="message-await-reply__text">
                  <span className="checkbox-label">Await Reply</span>
                  <span className="message-await-reply__help">
                    <span className="message-await-reply__help-line">
                      Check the box when a reply from
                    </span>
                    <span className="message-await-reply__help-line">
                      the Pet Parent will help with follow-up.
                    </span>
                    <span className="message-await-reply__help-line">
                      The Reply column will show &quot;
                      <span className="message-await-reply__status-preview">
                        <span className="dot yellow" aria-hidden="true" />
                        Await Reply
                      </span>
                      &quot;.
                    </span>
                  </span>
                </span>
              </label>

              <div className="attachment-uploader">
                <input
                  disabled={isLoading}
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                  accept="image/jpeg,image/png,application/pdf"
                  multiple
                />
                <button
                  type="button"
                  className={classNames(
                    'add-attachment-button nav-link-style',
                    isLoading && 'disabled',
                  )}
                  disabled={isLoading}
                  onClick={() => fileInputRef.current.click()}
                >
                  <img
                    src={attachmentIcon}
                    alt=""
                    className="add-attachment-button__icon"
                    aria-hidden
                  />
                  <span>Attach images or PDFs</span>
                </button>
              </div>
            </div>

            <ul
              className={classNames(
                'attachments-list',
                (attachments.images.length > 0 ||
                  attachments.pdfs.length > 0) &&
                  'visible',
              )}
            >
              {attachments.images.map((file, index) => (
                <li key={`img-${index}`} className="attachment-item">
                  <div className="attachment-preview">
                    <img
                      src={
                        typeof file === 'string'
                          ? file
                          : URL.createObjectURL(file)
                      }
                      alt={file.name}
                    />
                  </div>
                  <button
                    disabled={isLoading}
                    className="remove-attachment-button"
                    onClick={() => removeAttachment('images', index)}
                  >
                    <RemoveIcon />
                  </button>
                </li>
              ))}
              {attachments.pdfs.map((file, index) => (
                <li key={`pdf-${index}`} className="attachment-item">
                  <div className="attachment-preview pdf-preview">
                    <img src={pdfFileIcon} alt="PDF" />
                  </div>
                  <p className="attachment-name" title={file.name}>
                    {file.name}
                  </p>
                  <button
                    disabled={isLoading}
                    className="remove-attachment-button"
                    onClick={() => removeAttachment('pdfs', index)}
                  >
                    <RemoveIcon />
                  </button>
                </li>
              ))}
            </ul>

            {fileUploadErrors.length > 0 && (
              <div className="upload-errors">
                {fileUploadErrors.map((error, index) => (
                  <p
                    key={index}
                    className="upload-attachments-error text-danger"
                  >
                    {error}
                  </p>
                ))}
              </div>
            )}

            <div className="text-above-buttons">
              {textAboveButtons && !isLoading && (
                <p className="text-danger">{textAboveButtons}</p>
              )}
              {isLoading && hasAttachments && (
                <div className="loading-message">
                  <Spinner />
                  <span>Uploading attachments...</span>
                </div>
              )}
            </div>
          </div>

          <FooterButtons
            onClose={requestClose}
            showSaveButton={true}
            onSave={handleSave}
            closeText="Cancel"
            saveText="Send"
            saveDisabled={isLoading || isMessageTooLong}
            closeDisabled={isLoading}
          />
        </ModalBody>
      </Modal>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onConfirm={handleClose}
        onCancel={() => setIsConfirmModalOpen(false)}
      />
    </>
  );
}
